import { useCallback, useEffect, useState } from "react";
import { Database, Plus, Upload, Ban, GitBranch } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const EMPTY_FACTOR = {
  factor_code: "", factor_name: "", activity_category: "", fuel_type: "",
  ghg_scope: "SCOPE_1", factor_value: "", numerator_unit: "kgCO2e",
  denominator_unit: "kWh", geography: "", country: "", region: "",
  valid_year: new Date().getFullYear(), valid_from: "", valid_to: "",
  source_authority: "", source_document: "", source_version: "", source_url: "",
  methodology: "", gwp_version: "", notes: "",
};

const STATUS_STYLES = {
  ACTIVE: "bg-green-50 text-green-800 border-green-300",
  SUPERSEDED: "bg-slate-100 text-slate-600 border-slate-300",
  INACTIVE: "bg-red-50 text-red-800 border-red-300",
};

export default function AdminFactors() {
  const { user } = useAuth();
  const [factors, setFactors] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [filters, setFilters] = useState({ category: "ALL", status: "ALL" });
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY_FACTOR);
  const [usage, setUsage] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [importReport, setImportReport] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filters.category !== "ALL") params.category = filters.category;
      if (filters.status !== "ALL") params.status = filters.status;
      const [{ data: f }, { data: c }] = await Promise.all([
        api.get("/admin/emission-factors", { params }),
        api.get("/admin/unit-conversions"),
      ]);
      setFactors(f);
      setConversions(c);
    } catch (err) {
      toast.error("Errore caricamento", { description: formatApiError(err) });
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  if (!user?.is_superadmin) {
    return (
      <div className="max-w-xl" data-testid="admin-factors-forbidden">
        <Card className="border-slate-200"><CardContent className="p-6 text-sm text-slate-600">
          Sezione riservata al Super Admin della piattaforma.
        </CardContent></Card>
      </div>
    );
  }

  const openNewVersion = async (f) => {
    const { data } = await api.get(`/admin/emission-factors/${f.id}/usage`);
    setUsage(data.calculations_using);
    const { id, status, created_at, updated_at, previous_version_id, ...rest } = f;
    setForm({ ...EMPTY_FACTOR, ...rest, factor_code: `${f.factor_code}.V${new Date().getFullYear()}` });
    setDialog("version");
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, factor_value: parseFloat(form.factor_value), valid_year: parseInt(form.valid_year, 10) };
      if (dialog === "version") {
        const parentCode = form.factor_code;
        const parent = factors.find((f) => f.factor_code === form._parent_code);
        const { data } = await api.post(`/admin/emission-factors/${form._parent_id}/new-version`, payload);
        toast.success("Nuova versione creata", {
          description: data.previous_version_usage > 0
            ? `Il fattore precedente era usato in ${data.previous_version_usage} calcoli: resta integro per lo storico.`
            : "Versione precedente archiviata (SUPERSEDED).",
        });
      } else {
        await api.post("/admin/emission-factors", payload);
        toast.success("Fattore creato");
      }
      setDialog(null);
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const deactivate = async (f) => {
    try {
      const { data } = await api.put(`/admin/emission-factors/${f.id}/deactivate`);
      toast.success("Fattore disattivato", {
        description: data.calculations_using > 0 ? `Attenzione: utilizzato in ${data.calculations_using} calcoli storici (snapshot preservati).` : undefined,
      });
      load();
    } catch (err) {
      toast.error("Errore", { description: formatApiError(err) });
    }
  };

  const doImport = async (commit) => {
    try {
      const { data } = await api.post("/admin/emission-factors/import", { csv_text: csvText, commit });
      setImportReport(data);
      if (commit) {
        toast.success("Import completato", { description: `${data.inserted.length} fattori inseriti` });
        load();
      }
    } catch (err) {
      toast.error("Errore import", { description: formatApiError(err) });
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-factors-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Database className="w-7 h-7 text-green-700" aria-hidden="true" />
            Emission Factor Registry
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Fattori di emissione versionati con fonte, geography e anno di validità. I calcoli storici conservano uno snapshot immutabile.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setForm(EMPTY_FACTOR); setDialog("create"); }} data-testid="create-factor-button">
            <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nuovo fattore
          </Button>
          <Button variant="outline" onClick={() => { setDialog("import"); setImportReport(null); setCsvText(""); }} data-testid="import-factors-button">
            <Upload className="w-4 h-4 mr-1" aria-hidden="true" /> Importa CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
          <SelectTrigger className="w-[220px]" data-testid="filter-factor-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tutte le categorie</SelectItem>
            {["ELECTRICITY", "NATURAL_GAS", "DIESEL", "PETROL", "LPG", "REFRIGERANT", "HEAT_PURCHASED"].map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-[180px]" data-testid="filter-factor-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tutti gli stati</SelectItem>
            {["ACTIVE", "SUPERSEDED", "INACTIVE"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="factors">
        <TabsList data-testid="admin-factors-tabs">
          <TabsTrigger value="factors" data-testid="tab-factors">Fattori di emissione</TabsTrigger>
          <TabsTrigger value="conversions" data-testid="tab-conversions">Conversioni unità</TabsTrigger>
        </TabsList>
        <TabsContent value="factors">
          <Card className="border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <Table data-testid="factors-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Codice</TableHead><TableHead>Nome</TableHead><TableHead>Valore</TableHead>
                    <TableHead>Unità</TableHead><TableHead>Geo</TableHead><TableHead>Anno</TableHead>
                    <TableHead>Fonte</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {factors.map((f) => (
                    <TableRow key={f.id} data-testid={`factor-row-${f.factor_code}`}>
                      <TableCell className="font-mono text-xs">{f.factor_code}</TableCell>
                      <TableCell className="max-w-[220px] text-sm">{f.factor_name}</TableCell>
                      <TableCell className="font-semibold">{f.factor_value}</TableCell>
                      <TableCell className="text-xs">{f.numerator_unit}/{f.denominator_unit}</TableCell>
                      <TableCell>{f.geography || "—"}</TableCell>
                      <TableCell>{f.valid_year}</TableCell>
                      <TableCell className="text-xs max-w-[200px]">{f.source_authority} {f.source_version ? `· ${f.source_version}` : ""}</TableCell>
                      <TableCell><Badge variant="outline" className={STATUS_STYLES[f.status]}>{f.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" title="Nuova versione" onClick={() => openNewVersion(f)} data-testid={`newversion-${f.factor_code}`}>
                            <GitBranch className="w-4 h-4" aria-hidden="true" />
                          </Button>
                          {f.status === "ACTIVE" && (
                            <Button size="sm" variant="ghost" className="text-red-700" title="Disattiva" onClick={() => deactivate(f)} data-testid={`deactivate-${f.factor_code}`}>
                              <Ban className="w-4 h-4" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="conversions">
          <Card className="border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <Table data-testid="conversions-table">
                <TableHeader>
                  <TableRow><TableHead>Da</TableHead><TableHead>A</TableHead><TableHead>Fattore</TableHead><TableHead>Fonte</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {conversions.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.from_unit}</TableCell><TableCell>{c.to_unit}</TableCell>
                      <TableCell className="font-mono">{c.factor}</TableCell><TableCell>{c.source}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dialog === "create" || dialog === "version"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="factor-dialog">
          <DialogHeader>
            <DialogTitle>{dialog === "version" ? "Nuova versione del fattore" : "Nuovo fattore di emissione"}</DialogTitle>
            <DialogDescription>
              {dialog === "version" && usage != null && usage > 0
                ? `Attenzione: questo fattore è già utilizzato in ${usage} calcoli. La nuova versione non modifica i calcoli storici.`
                : "Nessun fattore può essere privo di fonte autorevole."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["factor_code", "Codice fattore *"], ["factor_name", "Nome *"],
              ["activity_category", "Categoria attività * (es. ELECTRICITY)"], ["fuel_type", "Combustibile"],
              ["numerator_unit", "Unità numeratore *"], ["denominator_unit", "Unità denominatore *"],
              ["geography", "Geografia (IT/EU/GLOBAL)"], ["valid_year", "Anno di validità *"],
              ["source_authority", "Fonte (autorità) *"], ["source_document", "Documento fonte"],
              ["source_version", "Versione fonte"], ["methodology", "Metodologia"],
              ["gwp_version", "Versione GWP (es. IPCC AR5)"],
            ].map(([key, label]) => (
              <div key={key} className="space-y-1.5">
                <Label>{label}</Label>
                <Input value={form[key] ?? ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  data-testid={`factor-field-${key}`} />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label>Valore fattore *</Label>
              <Input type="number" step="any" value={form.factor_value}
                onChange={(e) => setForm({ ...form, factor_value: e.target.value })} data-testid="factor-field-value" />
            </div>
            <div className="space-y-1.5">
              <Label>Scope</Label>
              <Select value={form.ghg_scope} onValueChange={(v) => setForm({ ...form, ghg_scope: v })}>
                <SelectTrigger data-testid="factor-field-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["SCOPE_1", "SCOPE_2_LOCATION", "SCOPE_2_MARKET"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setDialog(null)} data-testid="factor-cancel">Annulla</Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" data-testid="factor-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "import"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-2xl" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Importa fattori da CSV</DialogTitle>
            <DialogDescription>
              Intestazioni richieste: {["factor_code", "factor_name", "activity_category", "factor_value", "numerator_unit", "denominator_unit", "valid_year", "source_authority"].join(", ")}. Preview con validazione prima del commit.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={8} value={csvText} onChange={(e) => setCsvText(e.target.value)}
            placeholder="factor_code,factor_name,activity_category,factor_value,..."
            className="font-mono text-xs" data-testid="import-csv-input" />
          {importReport && (
            <div className="text-sm space-y-1 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="import-report">
              <p>Righe: {importReport.total} · Valide: {importReport.valid.length} · Errori: {importReport.errors.length} · Duplicati: {importReport.duplicates.length}</p>
              {importReport.errors.map((e, i) => <p key={i} className="text-red-700">Riga {e.row}: {e.error}</p>)}
              {importReport.duplicates.map((d, i) => <p key={i} className="text-amber-700">Riga {d.row}: duplicato {d.factor_code}</p>)}
              {importReport.inserted && <p className="text-green-800">Inseriti: {importReport.inserted.join(", ")}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => doImport(false)} data-testid="import-preview">Anteprima</Button>
            <Button className="bg-green-700 hover:bg-green-800" disabled={!importReport || importReport.preview !== true || importReport.valid.length === 0}
              onClick={() => doImport(true)} data-testid="import-commit">Importa {importReport?.valid?.length || 0} fattori</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
